import React from 'react';
import { Card, TextInput, IconButton } from '@parsina/framework';
import { Pill, Trash2 } from 'lucide-react';
import { C } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';

// One prescribed medicine, on a phone.
//
// The desktop row lays its four fields on a
// `repeat(auto-fit, minmax(140px, 1fr))` grid — two-up at best on a phone, with
// «مدت مصرف» truncated to «مدت». §6 is explicit: "full-width inputs, one per
// row". Four rows of one field each is more scroll and less guessing.
//
// EVERY FIELD CARRIES AN ARIA-LABEL THAT NAMES THE DRUG. The kit's `FormField`
// renders its `label` as a <span> with no htmlFor and no id, so the visible
// label is not attached to the control: without this each of these is an
// unnamed text box, and on a four-drug prescription that is sixteen of them —
// «دوز» alone would not say which drug's dose is being typed.

const FIELDS = [
  { key: 'dose', labelKey: 'prescription.item.dose' },
  { key: 'frequency', labelKey: 'prescription.item.frequency' },
  { key: 'duration', labelKey: 'prescription.item.duration' },
  { key: 'note', labelKey: 'prescription.item.note' },
];

const MobileRxCard = ({ item, onChange, onRemove, testId }) => {
  const { t } = useTranslation();

  return (
    <Card
      data-testid={testId}
      style={{ padding: '0.875rem', marginBottom: '0.625rem', borderRadius: '0.875rem' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <Pill size={16} color={C.teal} aria-hidden />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            dir="auto"
            style={{ fontSize: '0.8125rem', fontWeight: 800, color: C.textPrimary }}
          >
            {item.drug.name}
          </div>
          {(item.drug.genericName || item.drug.category) && (
            <div dir="auto" style={{ fontSize: '0.6875rem', color: C.textMuted }}>
              {[item.drug.genericName, item.drug.category].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
        {/* 44px, not the desktop's 36 (§6). */}
        <IconButton
          variant="ghost"
          size={44}
          icon={<Trash2 size={16} />}
          label={t('prescription.item.remove', { name: item.drug.name })}
          onClick={onRemove}
          data-testid={`${testId}-remove`}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
        {FIELDS.map(({ key, labelKey }) => (
          <TextInput
            key={key}
            label={t(labelKey)}
            aria-label={`${item.drug.name} — ${t(labelKey)}`}
            dir="auto"
            value={item[key]}
            onChange={(e) => onChange({ ...item, [key]: e.target.value })}
            data-testid={`${testId}-${key}`}
            // ≥1rem or iOS zooms the whole page the moment the field is
            // focused, and the dentist has to pinch back out (§6).
            inputStyle={{ fontSize: '1rem' }}
          />
        ))}
      </div>
    </Card>
  );
};

export default MobileRxCard;
